import { Popover } from '@ksyos/design-system';
import type { EpisodeCategory } from '@common/enums/episodeCategory';
import { EpisodeCategoryRightType, ServiceRightType } from '@common/interfaces/rights';
import type { IEmploymentAndServiceRights } from '../ServiceOrganizationPractitioners';
import { translateKey } from '../../../../../translate';
import { formatRight } from '../../../../../utils/formatRight';

interface IFormattedRightsProps {
    employment: IEmploymentAndServiceRights;
    episodeCategory: EpisodeCategory;
}

export function FormattedRights(props: IFormattedRightsProps) {
    const { employment, episodeCategory } = props;
    const formattedRights = formatRights(employment);

    if (formattedRights.length < 30) {
        return <>{formattedRights}</>;
    }

    return <Popover content={formattedRights}>{`${formattedRights.substring(0, 30)}...`}</Popover>;

    function formatRights(employmentRights: IEmploymentAndServiceRights) {
        const rights = employmentRights.activeRights || employmentRights.inactiveRights;
        if (!rights) {
            return '';
        }
        const formattedemploymentRights: string[] = [];
        if (rights.serviceEmploymentRights.length > 0) {
            for (const right of rights.serviceEmploymentRights) {
                const activities = right.serviceActivityAuthorizations.map(
                    (activity) =>
                        `${activity.serviceActivity.name} ${right.serviceName.toLowerCase()} (${
                            activity.canWrite
                                ? translateKey('addCarePathSettingsLabelsServiceEmployment.write')
                                : translateKey('addCarePathSettingsLabelsServiceEmployment.read')
                        })`
                );
                formattedemploymentRights.push(...activities);
            }
        }

        if (rights.serviceConsumerRights.length > 0) {
            for (const serviceConsumerRight of rights.serviceConsumerRights) {
                const right = {
                    ...serviceConsumerRight,
                    rightType: ServiceRightType.serviceConsumer,
                    service: toService(serviceConsumerRight),
                };
                formattedemploymentRights.push(formatRight(right));
            }
        }
        if (rights.serviceProviderRights.length > 0) {
            for (const serviceProviderRight of rights.serviceProviderRights) {
                const right = {
                    ...serviceProviderRight,
                    rightType: ServiceRightType.serviceProvider,
                    service: toService(serviceProviderRight),
                };
                formattedemploymentRights.push(formatRight(right));
            }
        }
        if (rights.serviceSupervisorRights.length > 0) {
            for (const serviceSupervisorRight of rights.serviceSupervisorRights) {
                const right = {
                    ...serviceSupervisorRight,
                    rightType: ServiceRightType.serviceSupervisor,
                    service: toService(serviceSupervisorRight),
                };
                formattedemploymentRights.push(formatRight(right));
            }
        }
        if (rights.serviceReaderRights.length > 0) {
            const right = {
                ...rights.serviceReaderRights[0],
                episodeCategory: { name: episodeCategory },
                rightType: EpisodeCategoryRightType.serviceReader,
            };
            formattedemploymentRights.push(formatRight(right));
        }

        return formattedemploymentRights.join(',\n');
    }

    function toService(serviceData: { serviceCode?: string; serviceName: string; serviceType?: string }) {
        return { code: serviceData.serviceCode, name: serviceData.serviceName, serviceType: serviceData.serviceType };
    }
}
